import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, ExternalLink, BookMarked, ToggleLeft, ToggleRight } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

const CATEGORIES = ["Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "SSC", "Banking", "General"];

export default function AdminPrintedBooks() {
  const [books, setBooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", mrp: "", price: "", description: "", image_url: "", category: "General",
    razorpay_url: "", pages: "", author: "", in_stock: true
  });

  useEffect(() => { fetchBooks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBooks = async () => {
    const { data } = await api.get("/printed-books");
    setBooks(data);
  };

  const resetForm = () => ({
    title: "", mrp: "", price: "", description: "", image_url: "", category: "General",
    razorpay_url: "", pages: "", author: "", in_stock: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, mrp: parseFloat(form.mrp) || 0, price: parseFloat(form.price) || 0 };
    if (editing) {
      await api.put(`/printed-books/${editing}`, payload);
    } else {
      await api.post("/printed-books", payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm(resetForm());
    fetchBooks();
  };

  const handleEdit = (book) => {
    setForm({
      title: book.title, mrp: book.mrp, price: book.price, description: book.description || "",
      image_url: book.image_url || "", category: book.category || "General",
      razorpay_url: book.razorpay_url || "", pages: book.pages || "", author: book.author || "",
      in_stock: book.in_stock !== false
    });
    setEditing(book.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this printed book?")) {
      await api.delete(`/printed-books/${id}`);
      fetchBooks();
    }
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-printed-books-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black admin-text flex items-center gap-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <BookMarked size={24} className="text-[#D97706]" />
              Printed Books
            </h1>
            <p className="text-sm admin-text-secondary mt-0.5">Manage hard copy books with Razorpay payment page links</p>
          </div>
          <button
            data-testid="add-printed-book-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm(resetForm()); }}
            className="bg-[#D97706] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
          >
            <Plus size={14} /> Add Printed Book
          </button>
        </div>

        {/* How it works */}
        {!showForm && books.length === 0 && (
          <div className="admin-card border p-5 mb-6">
            <h3 className="text-sm font-bold admin-text mb-3">How to add Printed Books:</h3>
            <ol className="text-xs admin-text-secondary space-y-2 list-decimal list-inside">
              <li>Go to <strong>Razorpay Dashboard → Payment Pages</strong> and create a payment page for each book</li>
              <li>Copy the <strong>Razorpay Payment Page URL</strong> (e.g., https://rzp.io/l/yourbook)</li>
              <li>Click <strong>"Add Printed Book"</strong> below and fill in book details</li>
              <li>Paste the Razorpay URL in the <strong>"Razorpay Payment Page URL"</strong> field</li>
              <li>Students will see an <strong>"Order Now"</strong> button that opens your Razorpay payment page</li>
            </ol>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-5 mb-6 space-y-4" data-testid="printed-book-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Book Title</label>
                <input data-testid="pb-title" placeholder="e.g., Primary TET English Grammar Book" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Author</label>
                <input data-testid="pb-author" placeholder="Author name" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">MRP (Original Price)</label>
                <input data-testid="pb-mrp" type="number" placeholder="₹499" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Selling Price</label>
                <input data-testid="pb-price" type="number" placeholder="₹299" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Category</label>
                <select data-testid="pb-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Pages</label>
                <input placeholder="e.g., 350 pages" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
              <textarea placeholder="Book description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-20`} />
            </div>

            <FileUpload
              label="Book Cover Image"
              accept="image/*"
              uploadType="printed-books"
              currentUrl={form.image_url}
              onUpload={(url) => setForm({ ...form, image_url: url })}
            />
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Or Paste Image URL</label>
              <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputClass} />
            </div>

            {/* Razorpay URL - IMPORTANT FIELD */}
            <div className="bg-amber-50 border border-amber-200 p-4">
              <label className="text-xs font-bold text-amber-700 flex items-center gap-1.5 mb-2">
                <ExternalLink size={14} /> Razorpay Payment Page URL
              </label>
              <input
                data-testid="pb-razorpay-url"
                placeholder="https://rzp.io/l/your-book-payment-link"
                value={form.razorpay_url}
                onChange={(e) => setForm({ ...form, razorpay_url: e.target.value })}
                className={`${inputClass} border-amber-300 focus:border-amber-500`}
                required
              />
              <p className="text-[10px] text-amber-600 mt-1">
                Create a payment page at <strong>Razorpay Dashboard → Payment Pages</strong> and paste the URL here. This link opens when students click "Order Now".
              </p>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">In Stock</label>
              <button type="button" onClick={() => setForm({ ...form, in_stock: !form.in_stock })} className={form.in_stock ? "text-green-500" : "text-slate-400"}>
                {form.in_stock ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
              </button>
              <span className="text-xs admin-text">{form.in_stock ? "In Stock" : "Out of Stock"}</span>
            </div>

            <div className="flex gap-2">
              <button data-testid="pb-submit" type="submit" className="bg-[#D97706] text-white font-bold py-3 px-6 text-sm transition-none">
                {editing ? "Update" : "Add"} Book
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="admin-card border font-bold py-3 px-6 text-sm transition-none admin-text">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Books List */}
        <div className="space-y-3" data-testid="printed-books-list">
          {books.map((book) => {
            const discount = book.mrp > book.price ? Math.round(((book.mrp - book.price) / book.mrp) * 100) : 0;
            return (
              <div key={book.id} className="admin-card border p-4 flex gap-4">
                <div className="w-20 aspect-[3/4] bg-slate-200 flex-shrink-0 overflow-hidden">
                  {book.image_url ? <img src={book.image_url} alt="" className="w-full h-full object-cover" /> : (
                    <div className="w-full h-full bg-[#D97706]/10 flex items-center justify-center"><BookMarked size={24} className="text-[#D97706]" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-bold admin-text">{book.title}</h3>
                      <p className="text-[10px] admin-text-secondary">{book.author || "—"} &middot; {book.category} &middot; {book.pages || "—"}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(book)} className="p-1.5 admin-text-secondary"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(book.id)} className="p-1.5 text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-black text-base admin-text">&#8377;{book.price}</span>
                    {discount > 0 && <>
                      <span className="text-xs admin-text-secondary line-through">&#8377;{book.mrp}</span>
                      <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5">{discount}%</span>
                    </>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`text-[10px] font-bold px-2 py-0.5 ${book.in_stock !== false ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {book.in_stock !== false ? "In Stock" : "Out of Stock"}
                    </span>
                    {book.razorpay_url && (
                      <a href={book.razorpay_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#0284C7] flex items-center gap-1">
                        <ExternalLink size={10} /> Payment Link
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          {books.length === 0 && !showForm && <p className="text-sm admin-text-secondary py-4 text-center">No printed books yet.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
